import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { OrgTreeActionsDirective } from '../directives/org-tree-actions.directive';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MailingService, SnackService } from '@shared/services';
import { OrganisationConfigurationService } from '../../../shared/services/organisation-configuration.service';
import { OrgTreeService } from '../services/org-tree.service';
import {
    beginningDate,
    currentDate,
    disableBackgroundScrolling,
    enableBackgroundScrolling
} from '../../../shared/helpers/helpers';
import { filter, Subscription, tap } from 'rxjs';
import { IOrganisationConfiguration } from '../../../shared/interfaces/responsible/IOrganisationConfiguration';
import { ITimelineItem, TimelineComponent } from './timeline/timeline.component';
import { OrganisationCommunicationType } from '../../../shared/enums/OrganisationCommunicationType';
import { ICommunicationTemplate } from '../../../shared/interfaces/responsible/ICommunicationConfiguration';
import { IMailRecipientItem, MailRecipientListComponent } from './mail-recipient-list/mail-recipient-list.component';
import {
    CcRecipientsDialogComponent,
    ICcRecipientsDialogData,
    ICcRecipientsDialogResult
} from './cc-recipients-dialog/cc-recipients-dialog.component';
import { ResponsibleType } from '@shared/interfaces';
import moment from 'moment';
import { Moment } from 'moment';
import { ModuleService } from '../../../shared/services/module.service';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../current-project';
import { MailTemplateIds } from '@monorepo/interfaces';

@Component({
    selector: 'app-org-communication-settings',
    templateUrl: './org-communication-settings.component.html',
    styleUrls: ['./org-communication-settings.component.scss'],
    standalone: false
})
export class OrgCommunicationSettingsComponent extends OrgTreeActionsDirective implements OnInit, OnDestroy {
    IS_TRS_PROJECT = IS_TRS_PROJECT;
    IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;

    subscriptions: Subscription[] = [];
    formGroup: UntypedFormGroup;
    timelineRef: MatDialogRef<TimelineComponent>;
    OrganisationCommunicationType = OrganisationCommunicationType;
    dates: ITimelineItem[] = [];
    dontValidate = false;
    openDialogs: Array<{
        dialogRef: MatDialogRef<MailRecipientListComponent>;
        communicationType: OrganisationCommunicationType;
    }> = [];

    private readonly mailingService = inject(MailingService);
    protected readonly mailTemplateIds: MailTemplateIds = {} as MailTemplateIds;

    constructor(
        private fb: UntypedFormBuilder,
        private snackService: SnackService,
        private organisationConfigurationService: OrganisationConfigurationService,
        private dialog: MatDialog,
        orgTreeService: OrgTreeService,
        moduleService: ModuleService
    ) {
        super(orgTreeService, moduleService);
    }

    get isMailTemplateIdsLoaded(): boolean {
        return Object.keys(this.mailTemplateIds).length > 0;
    }

    get inheritAllSettingsFromParentFormControl(): UntypedFormControl {
        return this.formGroup?.get('inheritAllSettingsFromParent') as UntypedFormControl;
    }

    get inheritAllSettingsFromParent(): boolean {
        return this.inheritAllSettingsFromParentFormControl.value;
    }

    set inheritAllSettingsFromParent(val: boolean) {
        this.inheritAllSettingsFromParentFormControl.patchValue(val);
    }

    get isEnabledFormControl(): UntypedFormControl {
        return this.formGroup?.get('isEnabled') as UntypedFormControl;
    }

    get isEnabled(): boolean {
        return this.isEnabledFormControl.value;
    }

    set isEnabled(val: boolean) {
        this.isEnabledFormControl.patchValue(val);

        if (val) {
            this.isEnabledFormControl.enable();
        } else {
            this.isEnabledFormControl.disable();
        }
    }

    get communicationTemplatesFormArray(): UntypedFormArray {
        return this.formGroup?.get('communicationTemplates') as UntypedFormArray;
    }

    get timeline(): TimelineComponent {
        return this.timelineRef?.componentInstance;
    }

    get specialistDate(): Moment {
        const date = moment(
            this.communicationTemplateDate(OrganisationCommunicationType.specialist) ||
                new Date(
                    this.currentConfiguration?.freezeDate ?? this.currentConfiguration?.maxFreezeDate
                ).copyAndAddDays(1)
        );

        return date.unix() <= moment(currentDate.atMidnight()).unix() ? moment(currentDate.copyAndAddDays(1)) : date;
    }

    get responsibleDate(): Moment {
        const date = moment(
            this.communicationTemplateDate(OrganisationCommunicationType.responsible) ||
                new Date(
                    this.currentConfiguration?.freezeDate ?? this.currentConfiguration?.maxFreezeDate
                ).copyAndAddDays(8)
        );
        return date.unix() <= moment(currentDate.atMidnight()).unix() ? moment(currentDate.copyAndAddDays(1)) : date;
    }

    get reminderDate(): Moment {
        const date = moment(
            this.communicationTemplateDate(OrganisationCommunicationType.reminder) ||
                new Date(this.currentConfiguration?.dueDate ?? this.currentConfiguration?.maxDueDate).copyAndAddDays(-7)
        );
        return date.unix() <= moment(currentDate.atMidnight()).unix() ? moment(currentDate.copyAndAddDays(1)) : date;
    }

    ngOnDestroy() {
        enableBackgroundScrolling();

        if (this.openDialogs?.length) {
            this.openDialogs.forEach(e => e.dialogRef.close());
        }

        this.subscriptions?.forEach(e => e?.unsubscribe());
        this.timelineRef.close();
    }

    async ngOnInit(): Promise<void> {
        disableBackgroundScrolling();

        this.buildForm();
        await this.loadMailTemplateIds();
    }

    private async loadMailTemplateIds(): Promise<void> {
        try {
            Object.assign(this.mailTemplateIds, await this.mailingService.getTemplateIds());
        } catch (error) {
            console.error('Failed to load mail template IDs:', error);
            this.snackService.openSnackBarError('Failed to load mail template IDs.');
        }
    }

    protected async saveConfiguration(): Promise<void> {
        if (!this.formGroup.valid) {
            this.snackService.openSnackBarError(`Please check your inputs.`);
            return;
        }

        const item = {
            id: this.currentConfiguration.id,
            communication: this.formGroup.getRawValue()
        } as IOrganisationConfiguration;
        try {
            this.currentConfiguration = await this.organisationConfigurationService.updateItem(item);
            this.snackService.openSnackBarSuccess('Success');
        } catch (e) {
            this.snackService.openSnackBarError(e.message);
        }
    }

    communicationTemplateIsEnabledFormControl(type: OrganisationCommunicationType): UntypedFormGroup {
        return this.communicationTemplateFormGroup(type)?.get('isEnabled') as UntypedFormGroup;
    }

    communicationTemplateFormGroup(type: OrganisationCommunicationType): UntypedFormGroup {
        return this.communicationTemplatesFormArray?.controls?.find(
            e => e?.get('typeOfCommunication')?.value === type
        ) as UntypedFormGroup;
    }

    communicationTemplateValue(type: OrganisationCommunicationType): ICommunicationTemplate {
        return this.communicationTemplateFormGroup(type)?.getRawValue();
    }

    get specialistDefaultTimeItem(): ITimelineItem {
        const isCorporate = this.rootNode.responsibleType === ResponsibleType.sag;
        if (IS_TRS_PROJECT) {
            const recipients = isCorporate ? 'TRSM-O all' : 'TRSM-RC Lead Countries';
            return {
                date: this.specialistDate,
                title: `Call for annual TR&S reports (${recipients})`,
                mailType: OrganisationCommunicationType.specialist,
                sortOrder: 1,
                onClick: item => this.showRecipients(item),
                tooltip: 'Simulate recipients'
            };
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            return {
                date: this.specialistDate,
                title: isCorporate ? 'timeline.specialistDate' : 'timeline.specialistDate.rc',
                mailType: OrganisationCommunicationType.specialist,
                sortOrder: 1,
                onClick: item => this.showRecipients(item),
                tooltip: 'Simulate recipients'
            };
        }
    }

    get responsibleDefaultTimeItem(): ITimelineItem {
        const isCorporate = this.rootNode.responsibleType === ResponsibleType.sag;
        if (IS_TRS_PROJECT) {
            const recipients = isCorporate ? 'TRSM-O all' : 'TRSM-RC Lead Countries';
            return {
                date: this.responsibleDate,
                title: `Call for annual TR&S reports (${recipients})`,
                mailType: OrganisationCommunicationType.responsible,
                sortOrder: 1,
                onClick: item => this.showRecipients(item),
                tooltip: 'Simulate recipients'
            };
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            return {
                date: this.responsibleDate,
                title: 'timeline.responsibleDate',
                mailType: OrganisationCommunicationType.responsible,
                sortOrder: 1,
                onClick: item => this.showRecipients(item),
                tooltip: 'Simulate recipients'
            };
        }
    }

    removeMailType(type: OrganisationCommunicationType) {
        this.timeline.data.dates = this.timeline.data.dates.filter(e => e.mailType !== type);
    }

    addMailType(item: ITimelineItem) {
        this.timeline.data.dates.push(item);
        this.timeline.sortItems();
    }

    communicationTemplateDateFormControl(type: OrganisationCommunicationType): UntypedFormGroup {
        return this.communicationTemplateFormGroup(type)?.get('sendAt') as UntypedFormGroup;
    }

    disableAllCommunications() {
        (this.communicationTemplatesFormArray.controls as UntypedFormGroup[]).forEach(e => {
            e?.get('isEnabled').patchValue(false);
            e?.get('isEnabled').disable();
            e?.get('sendAt').disable();
            e?.get('includeOtherArea').disable();
            e?.get('ccRecipients').disable();
        });
    }

    disableForCommunication(type: OrganisationCommunicationType) {
        const control = this.communicationTemplateFormGroup(type);
        control?.get('sendAt').disable();
        control?.get('includeOtherArea').disable();
        control?.get('ccRecipients').disable();
    }

    enableAllCommunications() {
        (this.communicationTemplatesFormArray.controls as UntypedFormGroup[]).forEach(e => {
            e?.get('isEnabled').patchValue(true);
            e?.get('isEnabled').enable();
            e?.get('sendAt').enable();
            e?.get('includeOtherArea').enable();
            e?.get('ccRecipients').enable();
        });
    }

    disableForInherited() {
        (this.communicationTemplatesFormArray.controls as UntypedFormGroup[]).forEach(e => {
            e?.get('isEnabled').patchValue(false);
            e?.get('isEnabled').disable();
            e?.get('sendAt').disable();
            e?.get('includeOtherArea').disable();
            e?.get('ccRecipients').disable();
        });
    }

    enableForCommunication(type: OrganisationCommunicationType) {
        this.dontValidate = true;

        const control = this.communicationTemplateFormGroup(type);
        control?.get('isEnabled').enable();

        this.dontValidate = false;
    }

    enableControlsForCommunication(type: OrganisationCommunicationType) {
        this.dontValidate = true;

        const control = this.communicationTemplateFormGroup(type);
        control?.get('isEnabled').patchValue(true);
        control?.get('isEnabled').enable();
        control?.get('sendAt').enable();
        control?.get('includeOtherArea').enable();
        control?.get('ccRecipients').enable();

        this.dontValidate = false;
    }

    enableCompletelyForCommunication(type: OrganisationCommunicationType) {
        this.dontValidate = true;

        const control = this.communicationTemplateFormGroup(type);
        control?.get('isEnabled').patchValue(true);
        control?.get('isEnabled').enable();
        control?.get('sendAt').enable();
        control?.get('includeOtherArea').enable();
        control?.get('ccRecipients').enable();

        this.dontValidate = false;
    }

    enableForInherited() {
        (this.communicationTemplatesFormArray.controls as UntypedFormGroup[]).forEach(e => {
            e?.get('isEnabled').patchValue(true);
            e?.get('isEnabled').enable();
            e?.get('sendAt').enable();
            e?.get('includeOtherArea').enable();
            e?.get('ccRecipients').enable();
        });
    }

    get reminderDefaultTimeItem(): ITimelineItem {
        const isCorporate = this.rootNode.responsibleType === ResponsibleType.sag;
        if (IS_TRS_PROJECT) {
            const recipients = isCorporate ? 'TRSM-O all' : 'TRSM-RC Lead Countries';
            return {
                date: this.reminderDate,
                title: `Friendly reminder mail (${recipients})`,
                mailType: OrganisationCommunicationType.reminder,
                sortOrder: 3,
                onClick: item => this.showRecipients(item),
                tooltip: 'Simulate recipients'
            };
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            return {
                date: this.reminderDate,
                title: isCorporate ? 'timeline.reminderDate' : 'timeline.reminderDate.rc',
                mailType: OrganisationCommunicationType.reminder,
                sortOrder: 3,
                onClick: item => this.showRecipients(item),
                tooltip: 'Simulate recipients'
            };
        }
    }

    get mailOrder(): OrganisationCommunicationType[] {
        let val = this.dates
            ?.filter(e => e.date && e.mailType && e.mailType !== OrganisationCommunicationType.reminder)
            .sort((a, b) => {
                return moment(a.date).unix() - moment(b.date).unix();
            })
            .map(e => e.mailType);

        if (val.length === 1) {
            val.push(
                val[0] === OrganisationCommunicationType.responsible
                    ? OrganisationCommunicationType.specialist
                    : OrganisationCommunicationType.responsible
            );
        } else if (!val.length) {
            val = [OrganisationCommunicationType.responsible, OrganisationCommunicationType.specialist];
        }

        return val;
    }

    communicationTemplateIsEnabled(type: OrganisationCommunicationType): boolean {
        return this.communicationTemplateIsEnabledFormControl(type)?.value;
    }

    communicationTemplateDate(type: OrganisationCommunicationType): Date {
        return (
            this.communicationTemplateDateFormControl(type)?.value ||
            this.communicationConfigurations?.communicationTemplates?.find(e => e.typeOfCommunication === type)?.sendAt
        );
    }

    changeDateForCommunication(formGroup: UntypedFormGroup, date: Date) {
        const communicationType = formGroup?.get('typeOfCommunication').value as OrganisationCommunicationType;
        this.timeline.changeDateForCommunication(communicationType, date);
    }

    copyParentCommunication() {
        const parentCommunication = this.currentConfiguration.parentCommunication;

        this.isEnabled = parentCommunication.isEnabled;

        for (const communication of parentCommunication.communicationTemplates) {
            this.communicationTemplateFormGroup(communication.typeOfCommunication).patchValue(communication);
        }
    }

    protected pickerSpecialistFilter(date: string): void {
        const shouldFilter =
            date &&
            moment(date).unix() <=
                moment(new Date(this.currentConfiguration?.dueDate ?? this.currentConfiguration?.maxDueDate))?.unix() &&
            moment(date).unix() >= moment(currentDate.copyAndAddDays(1).atMidnight()).unix();

        if (!shouldFilter) {
            this.snackService.openSnackBarError('Please select a date after the freeze date.');
            const dateFormControl = this.communicationTemplateDateFormControl(OrganisationCommunicationType.specialist);
            dateFormControl?.patchValue(null);
        }
    }

    protected pickerResponsibleFilter(date: string): void {
        const shouldFilter =
            date &&
            moment(date).unix() <=
                moment(new Date(this.currentConfiguration?.dueDate ?? this.currentConfiguration?.maxDueDate))?.unix() &&
            moment(date).unix() >= moment(currentDate.copyAndAddDays(1).atMidnight()).unix();

        if (!shouldFilter) {
            this.snackService.openSnackBarError('Please select a date after the freeze date.');
            const dateFormControl = this.communicationTemplateDateFormControl(
                OrganisationCommunicationType.responsible
            );
            dateFormControl?.patchValue(null);
        }
    }

    protected pickerReminderFilter(date: string): void {
        const shouldFilter =
            date &&
            moment(date).unix() >
                moment(this.communicationTemplateDate(OrganisationCommunicationType.specialist))?.unix() &&
            moment(date).unix() >
                moment(this.communicationTemplateDate(OrganisationCommunicationType.responsible))?.unix() &&
            moment(date).unix() <=
                moment(new Date(this.currentConfiguration?.dueDate ?? this.currentConfiguration?.maxDueDate))?.unix() &&
            moment(date).unix() >= moment(currentDate.copyAndAddDays(1).atMidnight()).unix();

        if (!shouldFilter) {
            this.snackService.openSnackBarError('Please select a date after the specialist and responsible dates.');
            const dateFormControl = this.communicationTemplateDateFormControl(OrganisationCommunicationType.reminder);
            dateFormControl?.patchValue(null);
        }
    }

    async showRecipients(item: ITimelineItem) {
        const communication = this.communicationTemplateValue(item.mailType);

        const recipients = await this.mailingService.simulateRecipients(
            this.rootNode.id ?? `responsibility-${this.rootNode.responsibleType}`,
            communication.typeOfCommunication
        );

        const allRecipients: IMailRecipientItem[] = recipients.map(e => {
            const organisation = this.rootNode.children.find(c => c.id === e.orgId);
            const allUsers = [
                ...(organisation.specialists ?? []),
                ...(organisation.responsible ? organisation.responsible : [])
            ];

            return {
                organisation,
                to: e.sendOption.to.map(r => allUsers.find(u => u.eMail === r)?.fullName).filter(Boolean),
                cc: e.sendOption.cc?.map(r => allUsers.find(u => u.eMail === r)?.fullName).filter(Boolean),
                bcc: e.sendOption.bcc?.map(r => allUsers.find(u => u.eMail === r)?.fullName).filter(Boolean)
            };
        });

        if (allRecipients?.length) {
            this.openRecipientDialog(communication, allRecipients, item);
        }
    }

    openRecipientDialog(
        communication: ICommunicationTemplate,
        recipients: IMailRecipientItem[],
        timelineItem: ITimelineItem
    ) {
        const dialog = this.openDialogs.find(e => e.communicationType === communication.typeOfCommunication);

        if (dialog) {
            dialog.dialogRef.close();
            return;
        }

        const dialogRef = this.dialog.open(MailRecipientListComponent, {
            position: { top: '50px' },
            panelClass: 'custom-dialog',
            backdropClass: 'transparent-backdrop',
            closeOnNavigation: true,
            disableClose: true,
            hasBackdrop: false,
            data: { recipients, communication, timelineItem }
        });

        dialogRef
            .afterClosed()
            .subscribe(_ => (this.openDialogs = this.openDialogs.filter(e => e.dialogRef !== dialogRef)));

        this.openDialogs.push({ dialogRef, communicationType: communication.typeOfCommunication });
    }

    setDates() {
        const isEnabled = this.formGroup ? this.isEnabled : this.communicationConfigurations?.isEnabled;
        this.dates = [
            {
                date: beginningDate,
                title: 'timeline.beginning',
                sortOrder: 0,
                icon: ['fas', 'play'],
                color: '#84a4c6'
                // color: '#e0e0e0'
            },
            {
                date: this.currentConfiguration?.freezeDate || this.currentConfiguration?.maxFreezeDate,
                title: 'timeline.freezeDate',
                sortOrder: 1,
                color: '#fecc46',
                icon: ['fas', 'snowflake']
            },
            isEnabled &&
            (this.communicationTemplateIsEnabled(OrganisationCommunicationType.specialist) ||
                this.communicationConfigurations?.communicationTemplates.find(
                    e => e.typeOfCommunication === OrganisationCommunicationType.specialist
                )?.isEnabled)
                ? this.specialistDefaultTimeItem
                : null,
            isEnabled &&
            (this.communicationTemplateIsEnabled(OrganisationCommunicationType.responsible) ||
                this.communicationConfigurations?.communicationTemplates.find(
                    e => e.typeOfCommunication === OrganisationCommunicationType.responsible
                )?.isEnabled)
                ? this.responsibleDefaultTimeItem
                : null,
            {
                title: 'timeline.reporting',
                hasNoDate: true,
                sortOrder: 2,
                color: '#326da2',
                icon: ['fas', 'file-lines']
            },
            isEnabled &&
            (this.communicationTemplateIsEnabled(OrganisationCommunicationType.reminder) ||
                this.communicationConfigurations?.communicationTemplates.find(
                    e => e.typeOfCommunication === OrganisationCommunicationType.reminder
                )?.isEnabled)
                ? this.reminderDefaultTimeItem
                : null,
            {
                date: this.currentConfiguration?.dueDate ?? this.currentConfiguration?.maxDueDate,
                title: 'timeline.dueDate',
                sortOrder: 4,
                color: '#60a64f',
                icon: ['fas', 'clock']
            }
        ].filter(e => e);
    }

    openCcRecipientsDialog(type: OrganisationCommunicationType): void {
        const formGroup = this.communicationTemplateFormGroup(type);
        const currentCcRecipients: string[] = formGroup?.get('ccRecipients')?.value ?? [];

        const dialogRef = this.dialog.open<
            CcRecipientsDialogComponent,
            ICcRecipientsDialogData,
            ICcRecipientsDialogResult
        >(CcRecipientsDialogComponent, {
            width: '500px',
            data: {
                communicationType: type,
                ccRecipients: currentCcRecipients
            }
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter(result => !!result),
                tap(result => {
                    formGroup?.get('ccRecipients')?.patchValue(result);
                })
            )
            .subscribe();
    }

    private buildForm() {
        this.setDates();

        this.timelineRef = this.dialog.open(TimelineComponent, {
            data: { dates: this.dates },
            panelClass: 'custom-max-width-dialog',
            position: { top: '20px' },
            backdropClass: 'transparent-backdrop',
            disableClose: true,
            hasBackdrop: false
        });

        // change timeline dates to reflect the dates in the form
        const templates = this.communicationConfigurations?.communicationTemplates;
        if (templates) {
            for (const e of templates) {
                const communicationType = e.typeOfCommunication;
                let newDate =
                    e.sendAt ??
                    (e.typeOfCommunication === OrganisationCommunicationType.specialist
                        ? this.specialistDefaultTimeItem?.date
                        : e.typeOfCommunication === OrganisationCommunicationType.responsible
                          ? this.responsibleDefaultTimeItem?.date
                          : this.reminderDefaultTimeItem?.date);

                // convert moment to date
                if (moment.isMoment(newDate)) {
                    newDate = newDate.toDate();
                }

                if (communicationType && newDate) {
                    this.timeline.changeDateForCommunication(communicationType, newDate);
                }
            }
        }

        this.formGroup = this.fb.group({
            isEnabled: {
                value: this.communicationConfigurations?.isEnabled ?? false,
                disabled: this.isOrgNode && this.communicationConfigurations?.inheritAllSettingsFromParent
            },
            inheritAllSettingsFromParent: {
                value: !this.isOrgNode
                    ? false
                    : (this.communicationConfigurations?.inheritAllSettingsFromParent ?? false),
                disabled: false
            },
            communicationTemplates: this.fb.array(
                this.communicationConfigurations?.communicationTemplates.map(e =>
                    this.fb.group({
                        typeOfCommunication: e.typeOfCommunication,
                        recipientArea: e.recipientArea,
                        includeOtherArea: {
                            value: e.includeOtherArea,
                            disabled: !e.isEnabled || this.communicationConfigurations?.inheritAllSettingsFromParent
                        },
                        sendAt: {
                            value:
                                e.sendAt ??
                                (e.typeOfCommunication === OrganisationCommunicationType.specialist
                                    ? this.specialistDefaultTimeItem?.date
                                    : e.typeOfCommunication === OrganisationCommunicationType.responsible
                                      ? this.responsibleDefaultTimeItem?.date
                                      : this.reminderDefaultTimeItem?.date),
                            disabled: !e.isEnabled || this.communicationConfigurations?.inheritAllSettingsFromParent
                        },
                        isEnabled: {
                            value: e.isEnabled,
                            disabled:
                                this.communicationConfigurations?.inheritAllSettingsFromParent ||
                                !this.communicationConfigurations?.isEnabled
                        },
                        ccRecipients: {
                            value: e.ccRecipients ?? [],
                            disabled: !e.isEnabled || this.communicationConfigurations?.inheritAllSettingsFromParent
                        }
                    })
                )
            )
        });

        this.subscriptions.push(
            this.communicationTemplateDateFormControl(OrganisationCommunicationType.specialist)?.valueChanges.subscribe(
                e =>
                    !this.dontValidate &&
                    !!e &&
                    this.changeDateForCommunication(
                        this.communicationTemplateFormGroup(OrganisationCommunicationType.specialist),
                        e
                    )
            ),
            this.communicationTemplateDateFormControl(OrganisationCommunicationType.reminder)?.valueChanges.subscribe(
                e =>
                    !this.dontValidate &&
                    !!e &&
                    this.changeDateForCommunication(
                        this.communicationTemplateFormGroup(OrganisationCommunicationType.reminder),
                        e
                    )
            ),
            this.communicationTemplateDateFormControl(
                OrganisationCommunicationType.responsible
            )?.valueChanges.subscribe(
                e =>
                    !this.dontValidate &&
                    !!e &&
                    this.changeDateForCommunication(
                        this.communicationTemplateFormGroup(OrganisationCommunicationType.responsible),
                        e
                    )
            ),
            this.communicationTemplateIsEnabledFormControl(
                OrganisationCommunicationType.specialist
            )?.valueChanges.subscribe(e => {
                if (!this.dontValidate) {
                    if (e) {
                        this.addMailType(this.specialistDefaultTimeItem);
                        this.enableControlsForCommunication(OrganisationCommunicationType.specialist);
                    } else {
                        this.removeMailType(OrganisationCommunicationType.specialist);
                        this.disableForCommunication(OrganisationCommunicationType.specialist);
                    }
                }
            }),
            this.communicationTemplateIsEnabledFormControl(
                OrganisationCommunicationType.responsible
            )?.valueChanges.subscribe(e => {
                if (!this.dontValidate) {
                    if (e) {
                        this.addMailType(this.responsibleDefaultTimeItem);
                        this.enableControlsForCommunication(OrganisationCommunicationType.responsible);
                    } else {
                        this.removeMailType(OrganisationCommunicationType.responsible);
                        this.disableForCommunication(OrganisationCommunicationType.responsible);
                    }
                }
            }),
            this.communicationTemplateIsEnabledFormControl(
                OrganisationCommunicationType.reminder
            )?.valueChanges.subscribe(e => {
                if (!this.dontValidate) {
                    if (e) {
                        this.addMailType(this.reminderDefaultTimeItem);
                        this.enableControlsForCommunication(OrganisationCommunicationType.reminder);
                    } else {
                        this.removeMailType(OrganisationCommunicationType.reminder);
                        this.disableForCommunication(OrganisationCommunicationType.reminder);
                    }
                }
            }),
            this.isEnabledFormControl.valueChanges.subscribe(e => {
                if (!this.dontValidate) {
                    if (!e) {
                        this.disableAllCommunications();
                    } else {
                        this.enableCompletelyForCommunication(OrganisationCommunicationType.responsible);
                        this.enableForCommunication(OrganisationCommunicationType.specialist);
                        this.enableForCommunication(OrganisationCommunicationType.reminder);
                    }

                    this.setDates();
                    this.timeline.data.dates = this.dates;
                }
            }),
            this.inheritAllSettingsFromParentFormControl.valueChanges.subscribe(e => {
                this.dontValidate = true;

                if (e) {
                    if (this.currentConfiguration?.parentCommunication) {
                        this.copyParentCommunication();
                    }

                    this.disableForInherited();
                    this.isEnabledFormControl.disable();
                } else {
                    this.isEnabled = false;
                    this.isEnabledFormControl.enable();
                }

                this.dontValidate = false;
            })
        );
    }

    protected templateId(type: OrganisationCommunicationType): string {
        return this.mailTemplateIds?.[this.rootNode?.responsibleType]?.[type];
    }
}
